import { sluggify } from '../../../../helpers/stringHelper';

/**
 * generateImageName
 *
 * @param  {string} name - file name
 * @param  {string} ext  - file extension
 * @param  {string} type - modifier added after file name, e.g. 'cropped' or 'original'
 * @returns {string} - a file name with no spaces in the format name_type.extension
 */
export function generateImageName(name, ext, type) {
    const imageName = name.split(".");
    let prefix = imageName[0];
    const fileType = ext || imageName[imageName.length - 1];

    // If the filename has periods in it, replace them with underscores
    if (imageName.length > 2) {
        imageName.pop();
        prefix = imageName.join('_');
    }

    prefix = sluggify(prefix);

    if (typeof type === 'string' && type.length) {
        return `${prefix}_${type}.${fileType}`;
    }

    return `${prefix}.${fileType}`;
}

export const optimizeImage = (image) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.src = image.source;

    let width = image.width;
    let height = image.height;

    // Resize the image if larger than iPhone 10 output size
    if (image.width > 4032 || image.height > 4032) {
        const largeEdge = image.width > image.height ? image.width : image.height;
        const factor = 4032 / largeEdge;
        width = Math.round(image.width * factor);
        height = Math.round(image.height * factor);
    }

    // If EXIF data indicates a rotation is needed, we flip the dimensions on
    // the canvas, but the other calculations need the native width and height
    const needsRotation = image.exifOrientation
        ? image.exifOrientation > 4 && image.exifOrientation < 9
        : false;

    canvas.width = needsRotation ? height : width;
    canvas.height = needsRotation ? width : height;

    // Perform transformations based on EXIF data
    switch (image.exifOrientation) {
        case 2:
            ctx.transform(-1, 0, 0, 1, width, 0);
            break;
        case 3:
            ctx.transform(-1, 0, 0, -1, width, height);
            break;
        case 4:
            ctx.transform(1, 0, 0, -1, 0, height);
            break;
        case 5:
            ctx.transform(0, 1, 1, 0, 0, 0);
            break;
        case 6:
            ctx.transform(0, 1, -1, 0, height, 0);
            break;
        case 7:
            ctx.transform(0, -1, -1, 0, height, width);
            break;
        case 8:
            ctx.transform(0, -1, 1, 0, 0, width);
            break;
        default: break;
    }

    ctx.drawImage(img, 0, 0, width, height);

    const dataURL = canvas.toDataURL('image/jpeg', 0.7);

    return new Promise((resolve) => {
        resolve({
            ...image,
            source: dataURL,
            width,
            height,
            size: Math.round((dataURL.length * 3) / 4)
        });
    });
};

export const cropImageToDataURL = (image, pixelCrop) => {
    const img = new Image();

    if (image.source.indexOf(';base64') === -1 && new URL(image.source).origin !== window.location.origin) {
        img.crossOrigin = 'anonymous';
    }

    img.src = image.source;

    const canvas = document.createElement('canvas');
    let width = pixelCrop.width;
    let height = pixelCrop.height;
    let factor = 1;

    if (pixelCrop.width > 2000 || pixelCrop.height > 2000) {
        const largeEdge = pixelCrop.width > pixelCrop.height ? pixelCrop.width : pixelCrop.height;
        factor = 2000 / largeEdge;

        width = Math.round(pixelCrop.width * factor);
        height = Math.round(pixelCrop.height * factor);
    }

    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(img, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, width, height);

    return new Promise((resolve) => {
        resolve({
            dataURL: canvas.toDataURL('image/jpeg', 0.7),
            cropData: {
                x: (pixelCrop.x / image.width) * 100,
                y: (pixelCrop.y / image.height) * 100,
                height: (pixelCrop.height / image.height) * 100,
                width: (pixelCrop.width / image.width) * 100
            }
        });
    });
};

function clamp(num, min, max) {
    return Math.min(Math.max(num, min), max);
}

/**
 * This method is plucked straight out of `react-image-crop` and provides the ability to convert a crop percentage to
 * the actual pixel values for the supplied image based on that image's dimensions. We have to pluck this method out
 * because we need to be able to perform the same calculation to establish the initial state in `PhotoCropper`.
 *
 * See https://github.com/DominicTobias/react-image-crop/blob/master/lib/ReactCrop.js#L94
 *
 * @param {Image} image The image that the crop is relative to.
 * @param {Object} percentCrop A crop data structure (e.g. { x: number, y: number, width: number, height: number })
 * @returns {{x: number, y: number, width: number, height: number}} A crop object.
 */
export const getPixelCrop = (image, percentCrop) => {
    const x = Math.round(image.naturalWidth * (percentCrop.x / 100));
    const y = Math.round(image.naturalHeight * (percentCrop.y / 100));
    const width = Math.round(image.naturalWidth * (percentCrop.width / 100));
    const height = Math.round(image.naturalHeight * (percentCrop.height / 100));

    return {
        x,
        y,
        // Clamp width and height so rounding doesn't cause the crop to exceed bounds.
        width: clamp(width, 0, image.naturalWidth - x),
        height: clamp(height, 0, image.naturalHeight - y)
    };
};


export function dataURLToBlob(dataURL) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    const byteString = atob(dataURL.split(',')[1]);

    // separate out the mime component
    const mimeString = dataURL
        .split(',')[0]
        .split(':')[1]
        .split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    const ab = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    const ia = new Uint8Array(ab);

    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    // write the ArrayBuffer to a blob, and you're done
    const blob = new Blob([ab], { type: mimeString });
    return blob;
}

/**
 * Takes an image file as a dataURL and returns an integer reflecting the
 * EXIF data orientation or returns null if image has no EXIF data.
 *
 * https://stackoverflow.com/questions/7584794/accessing-jpeg-exif-rotation-data-in-javascript-on-the-client-side
 */
export async function getOrientation(dataUrl) {
    const blob = dataURLToBlob(dataUrl);
    const arrayBuffer = await new Response(blob).arrayBuffer();
    const view = new DataView(arrayBuffer);

    if (view.getUint16(0, false) !== 0xFFD8) {
        return null;
    }
    const length = view.byteLength;
    let offset = 2;
    while (offset < length) {
        if (view.getUint16(offset + 2, false) <= 8) {
            return null;
        }
        const marker = view.getUint16(offset, false);
        offset += 2;
        if (marker === 0xFFE1) {
            // eslint-disable-next-line no-cond-assign
            if (view.getUint32(offset += 2, false) !== 0x45786966) {
                return null;
            }

            const little = view.getUint16(offset += 6, false) === 0x4949;
            offset += view.getUint32(offset + 4, little);
            const tags = view.getUint16(offset, little);
            offset += 2;
            for (let i = 0; i < tags; i++) {
                if (view.getUint16(offset + (i * 12), little) === 0x0112) {
                    return view.getUint16(offset + (i * 12) + 8, little);
                }
            }
        }
        // eslint-disable-next-line no-bitwise
        else if ((marker & 0xFF00) !== 0xFF00) {
            break;
        }
        else {
            offset += view.getUint16(offset, false);
        }
    }
    return null;
}